import { ISanitizedCompany } from 'types/company';
import { IUserResponse } from 'types/user';

export const collectionFactory = ({
  company,
  user,
  name,
  emoji,
}: {
  company: ISanitizedCompany;
  user: IUserResponse;
  name: string;
  emoji: string;
}) => {
  return {
    name,
    emoji,
    meta: {
      companyId: company.id,
      userId: user.uid,
      createdAt: new Date(),
      publishedAt: new Date(),
      public: false,
      team: [],
      showInFeed: false,
    },
    author: {
      firstName: user.profile.firstName,
      lastName: user.profile.lastName,
      avatar: user.profile.avatar,
    },
    position: [],
  };
};

// Orders a collection's stories by its saved `position` (Update Collection → Story Order).
// Stories missing from `position` — added or published after the order was saved — go last,
// keeping their incoming order.
export const sortStoriesByCollectionPosition = <T extends { id: string }>(
  stories: T[],
  position: string[] | undefined,
): T[] => {
  if (!position || position.length === 0) {
    return stories;
  }

  const rank = new Map(position.map((id, index) => [id, index]));
  const rankOf = (story: T) => rank.get(story.id) ?? position.length;

  return [...stories].sort((a, b) => rankOf(a) - rankOf(b));
};

// Merges a reordered subset of a collection's stories (e.g. the Story Order drawer under a
// topic filter) back into its saved `position`. Ids not in `orderedIds` keep their slots; the
// slots of the ids that are, plus one extra slot at the end per id `position` lacks, are filled
// in the user's order, so a new story keeps its place among the visible ones.
export const mergeStoryOrderIntoPosition = (
  orderedIds: string[],
  position: string[] | undefined,
): string[] => {
  const reordered = new Set(orderedIds);
  const queue = [...orderedIds];

  return [
    ...(position ?? []).map(id => (reordered.has(id) ? queue.shift()! : id)),
    ...queue,
  ];
};
